import moment from 'moment'

import { Shrine } from '@a4b/api/src/modules/Temple/types'

export type TithiOption = {
  value: string
  label: string
}

export const mergeTithiOptions = (
  tithiOptions: TithiOption[],
  editingRecord: any,
  selectedEntityId: string,
  tithiById?: Map<string, any>,
): TithiOption[] => {
  const options = Array.isArray(tithiOptions) ? tithiOptions : []
  
  if (editingRecord?.id) {
    const entityId = String(editingRecord.id)
    const exists = options.some(opt => opt?.value === selectedEntityId)
    
    if (!exists) {
      const tithi = tithiById?.get(entityId)
      const label = tithi?.name || tithi?.display_name_en || entityId
      return [{ value: entityId, label: `${label} (${entityId})` }, ...options]
    }
  }
  
  return options
}

export const getMediaUrl = (fileList: any[]): string => {
  if (!Array.isArray(fileList) || fileList.length === 0) return ''
  const file =
    fileList.find(x => x?.status === 'done') ?? fileList[fileList.length - 1]
  if (typeof file === 'string') return file
  return (
    file?.url ||
    file?.thumbUrl ||
    file?.response?.url ||
    file?.response?.data?.url ||
    file?.response?.Location ||
    file?.response?.data?.Location ||
    ''
  )
}

export const parseEventCalendarToFormFields = (
  record: Shrine.EventCalendar,
) => {
  return {
    // API returns ISO strings like 2025-12-31T07:26:48Z.
    // AntD DatePicker expects a moment-like value.
    event_date: record.event_date ? moment(record.event_date) : null,
    // Map API's entity_id to form's id field (tithiOptions are string ids).
    id: record.entity_id ? String(record.entity_id) : undefined,
    active: record.active,
    position: record.position,
  }
}

const formatEventCalendarDateForApi = (value: any): string => {
  if (!value) return ''
  // Backend expects UTC ISO with trailing Z and no milliseconds.
  // Format the date part and append T00:00:00Z to avoid timezone shifts
  return moment(value).format('YYYY-MM-DD') + 'T00:00:00Z'
}

export const formatEventCalendarCreatePayload = (
  values: any,
): Shrine.CreateEventCalendarPayload => {
  return {
    event_date: formatEventCalendarDateForApi(values.event_date),
    entity_id: values.id ? String(values.id) : '',
    active: !!values.active,
    position: Number(values.position ?? 0),
  }
}

export const formatEventCalendarUpdatePayload = (
  values: any,
  eventId: string,
): Shrine.UpdateEventCalendarPayload => {
  return {
    id: eventId,
    event_date: formatEventCalendarDateForApi(values.event_date),
    entity_id: values.id ? String(values.id) : '',
    active: !!values.active,
    position: Number(values.position ?? 0),
  }
}

export const parseCardToFormFields = (record: Shrine.Card) => {
  return {
    card_type: record.card_type,
    active: record.active,
    position: record.position,
    entity_ids: record.entity_ids || [],
  }
}

export const formatCardCreatePayload = (
  values: any,
  eventId: string,
): Shrine.CreateCardPayload => {
  return {
    event_id: eventId,
    card_type: values.card_type,
    active: !!values.active,
    position: Number(values.position ?? 0),
    entity_ids: values.entity_ids || [],
  }
}

export const formatCardUpdatePayload = (
  values: any,
  eventId: string,
  cardId: string,
): Shrine.UpdateCardPayload => {
  return {
    id: cardId,
    event_id: eventId,
    card_type: values.card_type,
    active: !!values.active,
    position: Number(values.position ?? 0),
  }
}

export const parseCardContentToFormFields = (record: Shrine.CardContent) => {
  return {
    title: record.title,
    display_name: record.display_name || '',
    description: record.description,
    language_code: record.language_code || (record as any).language_code || '',
    media_type: record.media?.media_type || '',
    media: record.media?.media_url ? [record.media.media_url] : [],
    active: record.active,
    cta: {
      text: record.cta?.text || (record.cta as any)?.type || '',
      deeplink: record.cta?.deeplink || '',
    },
  }
}

export const formatCardContentCreatePayload = (
  values: any,
  cardId: string,
): Shrine.CreateCardContentPayload => {
  const mediaUrl = getMediaUrl(values.media)
  const mediaType = values.media_type || values.media?.media_type || ''
  return {
    card_id: cardId,
    title: values.title,
    display_name: values.display_name || undefined,
    description: values.description,
    language_code: values.language_code || values.language_code || '',
    media: {
      media_type: mediaType,
      media_url: mediaUrl,
    },
    active: !!values.active,
    cta: {
      text: values.cta?.text || '',
      deeplink: values.cta?.deeplink || '',
    },
  }
}

export const formatCardContentUpdatePayload = (
  values: any,
  cardId: string,
  contentId?: string,
): Shrine.UpdateCardContentPayload => {
  const mediaUrl = getMediaUrl(values.media)
  const mediaType = values.media_type || values.media?.media_type || ''
  return {
    id: contentId,
    card_id: cardId,
    title: values.title,
    display_name: values.display_name || undefined,
    description: values.description,
    language_code: values.language_code || values.language_code || '',
    media: {
      media_type: mediaType,
      media_url: mediaUrl,
    },
    active: !!values.active,
    cta: {
      text: values.cta?.text || '',
      deeplink: values.cta?.deeplink || '',
    },
  }
}
